<template>
    <header
        class="flex items-center justify-between py-2 border-b border-gray-200 sm:px-6 sm:py-4 lg:px-0 dark:border-zinc-800 lg:flex-none"
    >
        <h1 class="text-lg font-semibold leading-6 text-gray-900 dark:text-zinc-50">
            <time v-if="meta?.shortTitle" :datetime="meta.dateTime" class="sm:hidden">{{ meta.shortTitle }}</time>
            <time :datetime="meta?.dateTime" :class="[meta?.shortTitle ? 'hidden sm:inline' : 'inline']">{{
                meta?.title
            }}</time>
            <p v-if="meta?.dayName" class="mt-1 text-sm text-gray-500">{{ meta.dayName }}</p>
        </h1>
        <div class="flex items-center">
            <div class="flex items-center rounded-md shadow-sm md:items-stretch">
                <button
                    type="button"
                    class="flex items-center justify-center py-2 pl-3 pr-4 text-gray-400 bg-white border border-r-0 border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-l-md hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50 dark:hover:bg-zinc-700 dark:hover:text-zinc-300 dark:md:hover:bg-zinc-700 dark:md:hover:text-zinc-300"
                    @click="previous"
                >
                    <span class="sr-only">Previous</span>
                    <icon icon="hero/outline/chevron-left" class="w-5 h-5" aria-hidden="true"></icon>
                </button>
                <button
                    type="button"
                    class="hidden border-t border-b border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-zinc-50 focus:relative md:block dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
                    @click="today"
                >
                    {{ lang('calendar.today') }}
                </button>
                <span class="relative w-px h-5 -mx-px bg-gray-300 dark:bg-zinc-900 md:hidden" />
                <button
                    type="button"
                    class="flex items-center justify-center py-2 pl-4 pr-3 text-gray-400 bg-white border border-l-0 border-gray-300 dark:border-zinc-700 dark:bg-zinc-800 rounded-r-md hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50 dark:hover:bg-zinc-700 dark:hover:text-zinc-300 dark:md:hover:bg-zinc-700 dark:md:hover:text-zinc-300"
                    @click="next"
                >
                    <span class="sr-only">Next</span>
                    <icon icon="hero/outline/chevron-right" class="w-5 h-5" aria-hidden="true"></icon>
                </button>
            </div>
            <div v-if="showViewSwitch" class="hidden md:ml-4 md:flex md:items-center">
                <Menu as="div" class="relative">
                    <MenuButton
                        type="button"
                        class="flex items-center py-2 pl-3 pr-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md shadow-sm dark:text-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700"
                    >
                        {{ lang(`calendar.views.${view}`) }}
                        <icon
                            icon="hero/outline/chevron-down"
                            class="w-5 h-5 ml-2 text-gray-400"
                            aria-hidden="true"
                        ></icon>
                    </MenuButton>

                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            class="absolute right-0 z-10 mt-3 overflow-hidden origin-top-right bg-white rounded-md shadow-lg dark:bg-zinc-800 w-36 ring-1 ring-black/5 focus:outline-hidden"
                        >
                            <div class="py-1">
                                <MenuItem v-slot="{ active }">
                                    <a
                                        href="#"
                                        :class="[
                                            active
                                                ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                                : 'text-gray-700 dark:text-zinc-300',
                                            'block px-4 py-2 text-sm',
                                        ]"
                                        @click="setView('day')"
                                        >{{ lang('calendar.views.day') }}</a
                                    >
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a
                                        href="#"
                                        :class="[
                                            active
                                                ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                                : 'text-gray-700 dark:text-zinc-300',
                                            'block px-4 py-2 text-sm',
                                        ]"
                                        @click="setView('week')"
                                        >{{ lang('calendar.views.week') }}</a
                                    >
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a
                                        href="#"
                                        :class="[
                                            active
                                                ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                                : 'text-gray-700 dark:text-zinc-300',
                                            'block px-4 py-2 text-sm',
                                        ]"
                                        @click="setView('month')"
                                        >{{ lang('calendar.views.month') }}</a
                                    >
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a
                                        href="#"
                                        :class="[
                                            active
                                                ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                                : 'text-gray-700 dark:text-zinc-300',
                                            'block px-4 py-2 text-sm',
                                        ]"
                                        @click="setView('year')"
                                        >{{ lang('calendar.views.year') }}</a
                                    >
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>
            <Menu v-if="showViewSwitch" as="div" class="relative ml-6 md:hidden">
                <MenuButton
                    class="flex items-center p-2 -mx-2 text-gray-400 border border-transparent rounded-full hover:text-gray-500"
                >
                    <span class="sr-only">Open menu</span>
                    <icon icon="hero/outline/dots-horizontal" class="w-5 h-5" aria-hidden="true"></icon>
                </MenuButton>

                <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                >
                    <MenuItems
                        class="absolute right-0 z-10 mt-3 overflow-hidden origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg dark:divide-zinc-900 dark:bg-zinc-800 w-36 ring-1 ring-black/5 focus:outline-hidden"
                    >
                        <div class="py-1">
                            <MenuItem v-slot="{ active }">
                                <a
                                    href="#"
                                    :class="[
                                        active
                                            ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                            : 'text-gray-700 dark:text-zinc-300',
                                        'block px-4 py-2 text-sm',
                                    ]"
                                    @click="setView('day')"
                                    >{{ lang('calendar.views.day') }}</a
                                >
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                                <a
                                    href="#"
                                    :class="[
                                        active
                                            ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                            : 'text-gray-700 dark:text-zinc-300',
                                        'block px-4 py-2 text-sm',
                                    ]"
                                    @click="setView('week')"
                                    >{{ lang('calendar.views.week') }}</a
                                >
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                                <a
                                    href="#"
                                    :class="[
                                        active
                                            ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                            : 'text-gray-700 dark:text-zinc-300',
                                        'block px-4 py-2 text-sm',
                                    ]"
                                    @click="setView('month')"
                                    >{{ lang('calendar.views.month') }}</a
                                >
                            </MenuItem>
                            <MenuItem v-slot="{ active }">
                                <a
                                    href="#"
                                    :class="[
                                        active
                                            ? 'bg-gray-100 text-gray-900 dark:text-zinc-50 dark:bg-zinc-700'
                                            : 'text-gray-700 dark:text-zinc-300',
                                        'block px-4 py-2 text-sm',
                                    ]"
                                    @click="setView('year')"
                                    >{{ lang('calendar.views.year') }}</a
                                >
                            </MenuItem>
                        </div>
                    </MenuItems>
                </transition>
            </Menu>
        </div>
    </header>
</template>

<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { toRef } from 'vue';

const emit = defineEmits(['setView', 'setDate']);

const setView = (view: string) => emit('setView', view);

const props = defineProps<{
    meta: {
        title: string;
        shortTitle?: string;
        selected: string;
        today: string;
        month: string;
        year: string;
        days: string[];
        firstDay: string;
        lastDay: string;
        dayName?: string;
        dateTime?: string;
        previous: string;
        previousData: {
            day: number;
            month: number;
            year: number;
        };
        next: string;
        nextData: {
            day: number;
            month: number;
            year: number;
        };
        todayData: {
            day: number;
            month: number;
            year: number;
        };
    };
    view: string;
    showViewSwitch: boolean;
}>();

const meta = toRef(props, 'meta');
const view = toRef(props, 'view');

const showViewSwitch = toRef(props, 'showViewSwitch');

const previous = () => {
    if (meta.value?.previousData) {
        emit('setDate', meta.value.previousData);
    }
};

const next = () => {
    if (meta.value?.nextData) {
        emit('setDate', meta.value.nextData);
    }
};

const today = () => {
    if (meta.value?.todayData) {
        emit('setDate', meta.value.todayData);
    }
};
</script>
